import "./activity-dashboard.scss";

import * as React from "react";

import {cx} from "emotion";

import {cs, cst, cstAssign}    from "@Common/react/chain-services";
import {consumeContext}        from "@Common/react/context";
import {Load2}                 from "@Common/react/load2";
import {scopedState}           from "@Common/react/scoped-state";
import {StorageState}          from "@Common/react/storage-state";
import {Types}                 from "@Common/types";
import {ButtonSpinner}         from "@Common/ui-components/loading-spinners/button-spinner";
import {SearchableMultiSelect} from "@Common/ui-components/searchable-multi-select/searchable-multi-select";
import {SearchableSelect}      from "@Common/ui-components/searchable-select/searchable-select";
import {mapValues, omit}       from "@Common/utils/objects";

import {COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

import {ReportsLayout} from "../common/reports-layout";

import {ReportModuleLoader} from "./reports-loader";

export const ActivityDashboard = () => cst<{
    apis                : Types.Apis.Loaders.XpressoLoadApis,
    branchesLoad        : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Branch.GetAll.Response>,
    regionsLoad         : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Region.GetList.Response>,
    reportModulesLoader : Types.Xpresso.Account.Admin.Reporting.ActivityDashboard.ReportModuleLoadsStateNext,
    settingsState       : Types.Common.React.StorageState.Next<{
        idBranch ?: string,
        idRegion ?: string,
        reports   : {[reportName:string]: {expanded?: boolean, tabIndex?: number}},
    }>,
    userLevels          : Types.Apis.Loaders.LoadUserLevelsNext,
}>(
    consumeContext("apis"),
    consumeContext("userLevels"),
    cstAssign("branchesLoad", ({apis, userLevels: {userLevel}}, next) => (
        Load2({next, fetch: userLevel === COMPANY_LEVEL ? apis.next.getBranchList : async () => []})
    )),
    cstAssign("regionsLoad", ({apis, userLevels: {userLevel}}, next) => (
        Load2({next, fetch: userLevel === COMPANY_LEVEL ? apis.next.region.getRegionList : async () => []})
    )),
    cstAssign("settingsState", ({userLevels: {idUser}}, next) => (
        StorageState({next, defaultValue: {reports: {}}, key: makeStorageKey({idUser}), storage: window.localStorage})
    )),
    cstAssign("reportModulesLoader", ({settingsState: {value: {idBranch, idRegion, reports}}, userLevels: {idUser}}, next) => (
        ReportModuleLoader({
            next,
            key: idUser,
            // custom report params may be incorporated here
            reportParamsMap: mapValues(reports, () => ({idBranch, idRegion})),
        }))
    ),
    ({
        branchesLoad         : {value: branches},
        regionsLoad          : {value: regions},
        reportModulesLoader  : {getOptions: getReportOptions, get: getReport, reload: reloadReports},
        settingsState,
        userLevels           : {userLevel},
    }) => {
        const {onChange: setBranchId, value: selectedIdBranch} = scopedState(settingsState, ["idBranch"]);
        const {onChange: setRegionId, value: selectedIdRegion} = scopedState(settingsState, ["idRegion"]);

        const reportSettingsState                                  = scopedState(settingsState, ["reports"]);
        const {onChange: setReportSettings, value: reportSettings} = reportSettingsState;

        const reportOptions        = (
            getReportOptions()
                .filter(({supportsBranchLevel}) => (
                    supportsBranchLevel
                    || (userLevel === COMPANY_LEVEL && !selectedIdBranch)
                ))
        );
        const selectedReportsCount = reportSettings ? Object.keys(reportSettings).length : 0;

        return (
            <div className="activity-dashboard-662e">
                <section>
                    <header>
                        Show Activity By...
                    </header>
                    <div className="config-ui-container">
                        {userLevel === COMPANY_LEVEL && (
                            <div className="row">
                                {regions?.length > 0 && (
                                    <SearchableSelect
                                        list={regions ?? []}
                                        getLabel={({name}) => name}
                                        isSelected={({idRegion}) => idRegion === selectedIdRegion}
                                        onSelect={(region) => setRegionId(region?.idRegion)}
                                        placeholder="All regions"
                                        allowClear={true}
                                        className="searchable-select"
                                        xdQaName="Region"
                                    />
                                )}
                                <SearchableSelect
                                    list={branches?.filter(branch => !selectedIdRegion || branch.idRegion === selectedIdRegion) ?? []}
                                    getLabel={({branchName}) => branchName}
                                    isSelected={({idBranch}) => idBranch === selectedIdBranch}
                                    onSelect={(branch) => {
                                        setBranchId(branch?.idBranch);
                                        setReportSettings(omit(
                                            reportSettings,
                                            reportOptions
                                                .filter(({supportsBranchLevel}) => !supportsBranchLevel)
                                                .map(({name}) => name)
                                        ));
                                    }}
                                    placeholder="All offices"
                                    allowClear={true}
                                    className="searchable-select"
                                    xdQaName="Branch"
                                />
                            </div>
                        )}
                        <div className="row">
                            <SearchableMultiSelect
                                list={reportOptions}
                                getLabel={({title}) => title}
                                isSelected={({name}) => !!reportSettings?.[name]}
                                onRemove={({name}) => setReportSettings(omit(reportSettings, [name]))}
                                onSelect={({name}) => setReportSettings({...reportSettings, [name]: {}})}
                                onSelectAll={() => setReportSettings(
                                    selectedReportsCount > 0
                                        ? {}
                                        : Object.fromEntries(reportOptions.map(({name}) => [name, {}]))
                                )}
                                placeholder="Click to select reports"
                                className="searchable-select wide"
                                xdQaName="Activity Select"
                                getXdQaName={(item) => item.title}
                            />
                            {selectedReportsCount > 0 && (
                                <div className="actions">
                                    <a onClick={() => setReportSettings({})} xd-qa-type="link" xd-qa-name="Clear all">Clear all ({selectedReportsCount})</a>
                                    <a onClick={() => reloadReports()} xd-qa-type="link" xd-qa-name="Refresh">Refresh</a>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {
                    selectedReportsCount
                    ?
                    Object.keys(reportSettings).map((name) => (
                        <ReportSection key={name} state={scopedState(reportSettingsState, [name])} {...getReport(name)}/>
                    ))
                    :
                    null
                }
            </div>
        );
    }
);

export const ReportSection = ({loaded, tabs, title, state}:{
    loaded  : Types.Xpresso.Account.Admin.Reporting.ActivityDashboard.ReportModuleLoadResult,
    tabs   ?: string[],
    title   : string,
    state   : Types.Common.React.UseState.UseStateNext<{expanded?: boolean, tabIndex?: number}>,
}) => cs(
    ({}) => {
        const {onChange: setExpanded, value: expanded = true} = scopedState(state, ["expanded"]);
        const {onChange: setTabIndex, value: tabIndex = 0   } = scopedState(state, ["tabIndex"]);
        return (
            <section key={title} xd-qa-type="card" xd-qa-name={title}>
                <header>{title} <a onClick={() => setExpanded(!expanded)} xd-qa-type="link" xd-qa-name={expanded ? "Hide Details" : "Show Details"}>{expanded ? "Hide" : "Show"} Details</a></header>
                {expanded && (
                    <div className="report-results">
                        {!loaded ? <div className="loading-spinner"><ButtonSpinner/></div> : (
                            <>
                                {tabs?.length > 1 && <div className="tabs-ui">
                                    {tabs.map((title, index) => (
                                        <button 
                                            key={index} 
                                            className={cx({active: tabIndex === index})} 
                                            onClick={() => setTabIndex(index)} 
                                            xd-qa-type="button" 
                                            xd-qa-name={tabIndex === index ? `${title} selected` : title}>{title}
                                        </button>
                                    ))}
                                </div>}
                                <table>
                                    <tbody>
                                        {loaded[tabIndex as number].map((row, index) => (
                                            <tr key={index} xd-qa-type="card" xd-qa-name={row.title}>
                                                <th scope="row" xd-qa-type="label" xd-qa-name={row.title}>{row.title}</th>
                                                <td xd-qa-type="text" xd-qa-name="Quantity">{row.content}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                )}
            </section>
        );
    }
);

export const AccountActivityDashboardRoute = () => cs(
    ({}, next) => ReportsLayout({activeTab: "activity-dashboard", next}),
    () => (
        <div className="activity-dashboard-route-662e"><ActivityDashboard/></div>
    ),
);

const makeStorageKey = ({idUser}:{idUser: number}) => `activity-dashboard-settings-d2eaeb5c-${idUser}`;

